/**
 * Maps raw API / network errors to friendly, user-facing copy.
 *
 * Errors reaching here come from `apiClient` (which sets `err.status` and
 * `err.responseData`) or from thrown Errors in the analysis flow. We never want
 * to surface raw strings like "Request failed: 500" or "Failed to fetch".
 *
 * Returns: { title, message, kind, retryable }
 *   kind      — coarse category, useful for picking an icon/CTA later
 *   retryable — whether offering a "Try again" action makes sense
 *
 * Resolution order: known backend error_code → HTTP status → substring
 * heuristics → safe generic fallback. When the backend starts returning a
 * stable `error_code`, that path takes priority so copy changes never break
 * detection (extend CODE_MAP rather than string-matching messages).
 */

const GENERIC = {
  title: "Something went wrong",
  message: "Something went wrong on our end. We've been notified — please try again in a moment.",
  kind: "server",
  retryable: true,
};

const TIMEOUT = {
  title: "This is taking longer than usual",
  message:
    "Our analyzer is busy right now. Your report may still be generating — check the History page in a few minutes, or try again.",
  kind: "timeout",
  retryable: true,
};

const NETWORK = {
  title: "Connection problem",
  message: "We couldn't reach our servers. Check your internet connection and try again.",
  kind: "network",
  retryable: true,
};

const URL_PROBLEM = {
  title: "Couldn't read that URL",
  message:
    "We couldn't access that privacy policy URL. Make sure it's correct and publicly reachable, then try again.",
  kind: "url",
  retryable: true,
};

// Stable backend error codes → copy. Backend may not send these yet; this is
// the forward-compatible path. Keys should match whatever `error_code` the API
// returns in its error payload.
const CODE_MAP = {
  INVALID_URL: URL_PROBLEM,
  URL_UNREACHABLE: URL_PROBLEM,
  POLICY_FETCH_FAILED: URL_PROBLEM,
  POLICY_NOT_FOUND: {
    title: "No policy found",
    message:
      "We couldn't find a privacy policy at that address. Double-check the link or upload the document instead.",
    kind: "url",
    retryable: true,
  },
  RATE_LIMITED: {
    title: "Slow down a moment",
    message: "You've made a lot of requests in a short time. Please wait a minute and try again.",
    kind: "rate",
    retryable: true,
  },
  ANALYSIS_FAILED: {
    title: "Analysis didn't complete",
    message:
      "We hit a snag while analyzing this policy. Please try again — if it keeps happening, contact support.",
    kind: "analysis",
    retryable: true,
  },
};

// A raw server string is only worth showing if it reads like a human sentence,
// not a stack trace / status code / serialized object.
const isHumanReadable = (s) => {
  if (!s || s.length > 160) return false;
  const low = s.toLowerCase();
  const bad = ["request failed", "failed to fetch", "econn", "etimedout", "undefined", "null", "<", "{", "}", "stack", "syntaxerror"];
  if (bad.some((b) => low.includes(b))) return false;
  // Require at least one space — single tokens like "Bad_Request" aren't copy.
  return /\s/.test(s.trim());
};

export const toFriendlyError = (err) => {
  if (!err) return GENERIC;

  const status = err.status ?? null;
  const code =
    err.responseData?.error_code ||
    err.responseData?.data?.error_code ||
    err.code ||
    null;
  const raw = (err.message || "").trim();
  const low = raw.toLowerCase();

  // 1. Known backend code wins.
  if (code && CODE_MAP[code]) return { ...CODE_MAP[code] };

  // 2. HTTP status.
  if (status === 0) return { ...NETWORK };
  if ([408, 502, 503, 504, 524].includes(status)) return { ...TIMEOUT };
  if (status === 429) return { ...CODE_MAP.RATE_LIMITED };
  if (status === 401 || status === 403)
    return {
      title: "Session expired",
      message: "Your session has expired. Please sign in again to continue.",
      kind: "auth",
      retryable: false,
    };
  if (status === 400 || status === 422)
    return {
      title: "Check your details",
      message: isHumanReadable(raw) ? raw : "Some details look off. Please review the form and try again.",
      kind: "validation",
      retryable: false,
    };
  if (status === 404)
    return {
      title: "Not found",
      message: "We couldn't find what you were looking for. It may have been removed.",
      kind: "notfound",
      retryable: false,
    };
  if (status === 409)
    // Conflict messages from the API are curated and specific (e.g. a name/URL
    // mismatch) — surface the server's wording in full rather than a generic line.
    return {
      title: "Check the details",
      message: raw || "This conflicts with something already saved in your account.",
      kind: "conflict",
      retryable: false,
    };
  if (typeof status === "number" && status >= 500) return { ...GENERIC };

  // 3. Substring heuristics for status-less / thrown errors.
  if (low.includes("network") || low.includes("failed to fetch") || low.includes("connection"))
    return { ...NETWORK };
  if (low.includes("timeout") || low.includes("timed out")) return { ...TIMEOUT };
  if (low.includes("url") && (low.includes("invalid") || low.includes("unreachable") || low.includes("fetch") || low.includes("reach")))
    return { ...URL_PROBLEM };

  // 4. Fallback — show the raw message only if it reads like a sentence.
  if (isHumanReadable(raw))
    return { title: "Analysis didn't complete", message: raw, kind: "server", retryable: true };

  return { ...GENERIC };
};

export default toFriendlyError;
